/**
 * Evidence Hub data adapters + view-state contract.
 *
 * Root cause of the 2026-07 "5 Total Evidence / No evidence found"
 * contradiction: the mounted PG evidence router returns
 * `{ evidence: [...], total }` (array key `evidence`, row id in
 * `evidence_id`, flat `approval_status`), while the page parsed `data.items`
 * and read the count from a SEPARATE `/statistics` endpoint. Result: the grid
 * silently rendered [] while the KPI showed the stats total.
 *
 * These pure helpers make the page render exactly what the list endpoint
 * returns, drive the "showing X of Y" line from that ONE response, and
 * classify the view state (loading / error / empty / filtered_empty / ready)
 * so an active filter or a failed fetch is never shown as "no evidence".
 * See docs/TRUST_STATE_MODEL.md and docs/METRIC_GLOSSARY.md.
 */

/** Pull the evidence array out of whatever shape the endpoint returns. */
export function extractEvidenceRows(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.evidence)) return data.evidence; // mounted PG router
  if (Array.isArray(data?.items)) return data.items;       // legacy Mongo shape
  return [];
}

/** The list endpoint's own total — the denominator for "X of Y", so the
 *  count and the grid can never come from different datasets. Falls back to
 *  the rendered length only when the endpoint omits a total. */
export function extractListTotal(data, rows) {
  const t = data?.total;
  return Number.isFinite(t) ? t : rows.length;
}

/**
 * Normalize one row to the shape the cards render. Tolerant of both the PG
 * router shape (evidence_id / name / approval_status / requirement_ids) and
 * the legacy hub shape (id / original_filename / ownership.approval_status /
 * framework_mappings), so cards get a stable id, a truthful status, and
 * framework chips regardless of which store answered.
 */
export function normalizeEvidence(r) {
  const id = r.id || r.evidence_id || r._id || null;
  const status = r.ownership?.approval_status || r.approval_status || r.status || 'uploaded';
  const framework_mappings = Array.isArray(r.framework_mappings)
    ? r.framework_mappings
    : (r.requirement_ids || []).map((code) => ({ framework_code: code }));
  return {
    ...r,
    id,
    original_filename: r.original_filename || r.name || r.title || 'Untitled',
    status,
    framework_mappings,
    category: r.category || r.evidence_type || null,
    uploaded_at: r.uploaded_at || r.created_at || null,
  };
}

/** True when any server-side or client-side filter is narrowing the view. */
export function hasActiveFilter({ searchQuery, filterStatus, filterFramework, filterCategory }) {
  return Boolean(searchQuery) ||
    filterStatus !== 'all' || filterFramework !== 'all' || filterCategory !== 'all';
}

/**
 * The view's data state. Mutually distinguishable so the UI never conflates
 * them — the whole point of the trust-state contract.
 *   loading | error | ready | filtered_empty | empty
 */
export function evidenceViewState({ loading, error, shownCount, filtersActive }) {
  if (loading) return 'loading';
  if (error) return 'error';
  if (shownCount > 0) return 'ready';
  return filtersActive ? 'filtered_empty' : 'empty';
}
